import { useContext, useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import classNames from 'classnames';

import config from 'config';
import { Name } from 'types/models/Name';
import { NameSchemaType } from 'types/models/NameSchemaType';
import { getVariantAttributesRoute } from 'apiRoutes/variants';
import { ConfigContext } from '@providers/ConfigProvider';
import { ProductContext } from '@providers/ProductProvider';
import { CartContext } from '@providers/CartProvider';
import ProductName from '@components/ProductName';
import StarRating from '@components/StarRating';
import BarAboveTitle from '@components/BarAboveTitle';
import ShowMoreContainer from '@components/ShowMoreContainer';
import Description from '@templates/Description';
import Attributes from '@templates/product/Attributes';
import ProductSpecificationKeyPress from '@components/keypress/ProductSpecificationKeyPress';
import CopyButton from '@components/CopyButton';
import VideoPlayer from '@components/VideoPlayer';
import Traits from '@components/Traits';
import ProductPhoto from '@templates/ProductPhoto';
import ManufacturerPartnership from '@templates/ManufacturerPartnership';
import { RecommendedProductsForProduct } from '@components/RecommendedProducts';
import PackageContent from '@components/PackageContent';
import DocumentsList from '@components/DocumentsList';
import ProductAttributesDescription from '@components/ProductAttributesDescription';
import VariantsList from '@components/VariantsList';
import ProductRating from '@components/ProductRating';
import Section from '@components/Section';
import getNameByType from 'utils/getNameByType';
import { FormattedMessage, useIntl } from 'utils/intl';
import getSerializedAttributes from 'utils/getSerializedAttributes';
import defer from 'utils/defer';
import useBoolean from 'hooks/useBoolean';
import useFetch from 'hooks/useFetch';

import ComplementaryProducts from './components/ComplementaryProducts';
import StickyMenuProduct from './components/StickyMenuProduct';
import ProductSection from './components/ProductSection';
import PurchaseBar from './components/PurchaseBar';
import VariantsSelectors from './components/VariantsSelector';
import { anchors } from './Product.consts';
import messages from './Product.messages';
import classes from './Product.module.scss';

const { routes } = config;

const getName = (names: Name[], type: NameSchemaType['id']) =>
  names.find(({ nameSchemaTypeId }) => nameSchemaTypeId === type);

type Props = {
  hits: number;
  isDescriptionNotEmpty: boolean;
};

const Product = ({ hits, isDescriptionNotEmpty }: Props): JSX.Element => {
  const intl = useIntl();
  const {
    id,
    product,
    manufacturer,
    productType,
    producerPn,
    productCode,
    thumbnail,
    video,
    names,
    tags,
    traits,
    description,
    pictures,
    urlSlug,
    attributes,
    attributesDescriptions,
    rating,
    ratingCount,
    isPrerelease,
    isEol,
    sectionsVisibility,
    recommendedVariants,
    complementaryVariants,
    packageContents,
    documents,
    variants,
    filters,
    constantFilters,
    selectedComplementaryProductsIds,
  } = useContext(ProductContext);
  const { ref: specificationRef, inView: isSpecificationInView } = useInView({ triggerOnce: true });
  const { ref: attributesDescriptionsRef, inView: areAttributesDescriptionsInView } = useInView({ triggerOnce: true });
  const [isInitialized, { on: turnOnIsInitialized }] = useBoolean(false);
  const [withFade, { on: turnOnWithFade }] = useBoolean(false);
  const [areAttributesFetched, { on: markAsFetched, off: markAsUnfetched }] = useBoolean(false);
  const { shopId } = useContext(ConfigContext);
  const { onAddToCart } = useContext(CartContext);
  const [getAttributes, { data: attributesData, loading }] = useFetch(getVariantAttributesRoute);

  const localAttributes = useMemo(() => attributesData?.attributes || attributes, [attributes, attributesData]);

  const localAttributesDescriptions = useMemo(
    () => attributesData?.attributesDescriptions || attributesDescriptions,
    [attributesDescriptions, attributesData]
  );

  const titleWithWordbreaks = !!(getName(names, 'PRODUCT_H1') || getName(names, 'TITLE'))?.nameSchema?.hasWordbreaks;

  const [firstPicture, secondPicture] = pictures;

  const getPageChangeHref = (page: number | undefined) => ({
    pathname: routes.product.id.href,
    query: { name: urlSlug || '-', productId: id, page },
  });

  useEffect(() => {
    defer(turnOnIsInitialized);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isInitialized) {
      defer(() => {
        turnOnWithFade();
        markAsUnfetched();
      });
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!areAttributesFetched && !loading && (areAttributesDescriptionsInView || isSpecificationInView)) {
      defer(() => {
        getAttributes({ id });
        markAsFetched();
      });
    }
  }, [id, areAttributesFetched, areAttributesDescriptionsInView, isSpecificationInView]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div id={anchors.pageStart} className={classes.container}>
      <div className={classes.topContainer}>
        <div
          className={classNames(classes.leftSide, {
            [classes.leftSideSpacing]: !manufacturer.partnership,
          })}
        >
          {!!pictures.length && (
            <div
              className={classNames(classes.productPhoto, {
                [classes.withSpacing]: !!tags.length,
                [classes.withMargin]: pictures.length > 1,
              })}
              data-nosnippet
            >
              <ProductPhoto
                id={id}
                productTypeId={productType.id}
                mainTitle={getNameByType(names, 'TITLE', shopId)}
                photos={pictures}
              />
            </div>
          )}
          {manufacturer.partnership && (
            <ManufacturerPartnership className={classes.productPartnership} partnership={manufacturer.partnership} />
          )}
        </div>
        <div key={id} className={classNames(classes.wrapper, { [classes.withFade]: withFade })}>
          <div>
            <div className={classes.header}>
              <div className={classes.barAboveTitle} data-nosnippet>
                <BarAboveTitle id={id} productTypeId={productType.id} tags={tags} />
              </div>
              <ProductName
                title={getNameByType(names, 'PRODUCT_H1', shopId) || getNameByType(names, 'TITLE', shopId)}
                productCode={productCode}
                producerPn={producerPn}
                manufacturer={manufacturer.name}
                withWordbreaks={titleWithWordbreaks}
              />
              <StarRating rating={rating} />
            </div>
            {!isPrerelease && (
              <div className={classes.configuration} data-nosnippet>
                <VariantsSelectors />
                {!!complementaryVariants.length && !isEol && <ComplementaryProducts />}
              </div>
            )}
            <PurchaseBar />
          </div>
        </div>
      </div>
      <StickyMenuProduct addToCart={() => onAddToCart(id, 1, selectedComplementaryProductsIds)} />
      <div className={classes.columnSections} anchor-id={anchors.description}>
        {video && (
          <Section title={<FormattedMessage {...messages.video} values={{ productName: product.name }} />}>
            <VideoPlayer video={video} />
          </Section>
        )}
        {!!traits.length && (
          <Section title={<FormattedMessage {...messages.traits} values={{ productName: product.name }} />}>
            <Traits traits={traits} picture={video ? undefined : secondPicture || firstPicture} />
          </Section>
        )}
      </div>
      {isDescriptionNotEmpty && description && (
        <Section title={<FormattedMessage {...messages.description} values={{ productName: product.name }} />}>
          <Description data={description} />
        </Section>
      )}
      {!!localAttributesDescriptions.length && (
        <Section
          ref={attributesDescriptionsRef}
          title={<FormattedMessage {...messages.attributesDescription} values={{ productName: product.name }} />}
        >
          <ShowMoreContainer containerHeight="300px" forceShowAll={localAttributesDescriptions.length < 4}>
            <ProductAttributesDescription attributes={localAttributesDescriptions} />
          </ShowMoreContainer>
        </Section>
      )}
      <ProductSpecificationKeyPress attributes={localAttributes} />
      <div anchor-id={anchors.specification} />
      {sectionsVisibility?.specification && (
        <Section
          ref={specificationRef}
          classes={{ header: classes.mainTitle }}
          title={<FormattedMessage {...messages.specification} values={{ productName: product.name }} />}
          actions={
            <CopyButton
              className={classes.copyButton}
              copyText={getSerializedAttributes(localAttributes, intl.formatMessage(messages.noneValue))}
              copyMessage={intl.formatMessage(messages.specificationCopied)}
            />
          }
        >
          <div className={classes.specification}>
            <Attributes attributes={localAttributes} mode="large" />
          </div>
        </Section>
      )}
      <div className={classes.columnSections} anchor-id={anchors.documentation}>
        {sectionsVisibility?.documentation && (
          <Section title={<FormattedMessage {...messages.documentation} values={{ productName: product.name }} />}>
            <DocumentsList documents={documents} />
          </Section>
        )}
        {!!packageContents.length && (
          <Section title={<FormattedMessage {...messages.packageContent} values={{ productName: product.name }} />}>
            <PackageContent className={classes.packageContent} packageContent={packageContents} />
          </Section>
        )}
      </div>
      {sectionsVisibility?.variants && (
        <ProductSection
          anchor={anchors.variants}
          title={<FormattedMessage {...messages.variants} values={{ productName: product.name }} />}
          classes={{ header: classes.variantsSectionHeader }}
        >
          <VariantsList
            key={product.id}
            productName={product.name}
            hits={hits}
            filters={filters}
            constantFilters={constantFilters}
            variants={variants}
            getPageChangeHref={getPageChangeHref}
            withFakeLink
          />
        </ProductSection>
      )}
      {sectionsVisibility?.rating && (
        <ProductSection
          anchor={anchors.rating}
          title={<FormattedMessage {...messages.rating} values={{ productName: product.name }} />}
        >
          <ProductRating
            id={id}
            name={getNameByType(names, 'VARIANT_TILE', shopId)}
            image={thumbnail}
            productCode={productCode}
            rating={rating}
            ratingCount={ratingCount}
          />
        </ProductSection>
      )}
      {sectionsVisibility?.recommendedVariants && (
        <ProductSection
          anchor={anchors.recommendedVariants}
          title={<FormattedMessage {...messages.accessories} values={{ productName: product.name }} />}
        >
          <RecommendedProductsForProduct
            listType="product_recommended_products"
            products={recommendedVariants}
            withScroll
          />
        </ProductSection>
      )}
    </div>
  );
};

export default Product;
